import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import { type ExecuteAction } from '@mls-wallet/shared-types';

/**
 * Response from execute endpoint after the async execution flow change.
 * Contains the guardianTransactionId for polling execution status.
 */
interface ExecuteProposalResponse {
  guardianTransactionId: string;
}

type ExecuteGroupProposalArg = {
  proposalId: string;
  action: ExecuteAction;
};

const fetcher = async (
  _key: string,
  { arg }: { arg: ExecuteGroupProposalArg }
): Promise<ExecuteProposalResponse> => {
  const { proposalId, action } = arg;
  return await denApi.post<ExecuteProposalResponse, { action: ExecuteAction }>(
    `admin/group/group-proposals/${encodeURIComponent(proposalId)}/execute`,
    {
      action,
    }
  );
};

/**
 * Hook to execute a group proposal (approve or reject).
 *
 * For APPROVE action:
 * - After calling executeProposal, the response contains a guardianTransactionId
 *   that should be used with useGuardianTransactionStatus to poll execution status.
 *
 * For REJECT action:
 * - Can only be called once the proposal has received enough rejection signatures.
 * - Returns a guardianTransactionId for async execution tracking via polling.
 *
 * @example
 * ```tsx
 * const { executeProposal } = useExecuteGroupProposal();
 * const { startExecution } = useExecution();
 *
 * const handleExecute = (action: ExecuteAction) => {
 *   startExecution({
 *     executionTarget: 'proposal',
 *     executionTargetId: proposalId,
 *     proposalType: 'group',
 *     request: () => executeProposal({ proposalId, action }),
 *   });
 * };
 * ```
 */
export function useExecuteGroupProposal(
  swrOptions?: SWRMutationConfiguration<
    ExecuteProposalResponse,
    Error,
    string,
    ExecuteGroupProposalArg
  >
) {
  const { trigger, isMutating, error } = useSWRMutation<
    ExecuteProposalResponse,
    Error,
    string,
    ExecuteGroupProposalArg
  >('admin/group/group-proposals/execute', fetcher, swrOptions);

  return { executeProposal: trigger, isLoading: isMutating, error };
}
